import { useRef, useState } from 'react';
import { REMOTE_DATASETS, type RemoteDataset } from '../samples/catalog';

interface EmptyStateProps {
  sampleExists: boolean;
  canManage: boolean;
  remoteBusy: boolean;
  onLoadSample: () => void;
  onImport: (file: File) => void;
  onPickRemote: (dataset: RemoteDataset) => void;
  onCustomUrl: (url: string) => void;
  onDismiss: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function EmptyState({
  sampleExists,
  canManage,
  remoteBusy,
  onLoadSample,
  onImport,
  onPickRemote,
  onCustomUrl,
  onDismiss,
}: EmptyStateProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [showRemote, setShowRemote] = useState(false);
  const [customUrl, setCustomUrl] = useState('');

  const submitCustom = () => {
    const trimmed = customUrl.trim();
    if (!trimmed || remoteBusy) return;
    onCustomUrl(trimmed);
    setCustomUrl('');
  };

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center overflow-auto bg-black/60 p-6 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-lg border border-[#4a4a4a] bg-[#3c3f41] p-5 shadow-2xl">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-2 top-2 rounded p-1 text-[14px] leading-none text-[#808080] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
          aria-label="Dismiss"
          title="Dismiss (you can still type a query)"
        >
          ×
        </button>
        <h3 className="mb-1 text-sm font-semibold text-[#e8e8e8]">Get started</h3>
        <p className="mb-4 text-[12px] text-[#bbbbbb]">
          Run a query above with ⌘/Ctrl+Enter, or seed something to play with:
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onLoadSample}
            className="flex items-start justify-between gap-3 rounded border border-[#323232] bg-[#2b2b2b] px-3 py-2 text-left transition-colors hover:border-[#499c54] hover:bg-[#4c5052]"
          >
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-[#e8e8e8]">
                {sampleExists ? 'Open sample database' : 'Load sample database'}
              </div>
              <div className="mt-0.5 text-[11px] text-[#808080]">
                {sampleExists
                  ? 'Switch to the existing sample (4 tables, ~30 rows)'
                  : '4 tables, ~30 rows — joins across employees, departments, projects, assignments'}
              </div>
            </div>
            <span className="shrink-0 self-center text-[#606366]">→</span>
          </button>

          <button
            type="button"
            onClick={() => setShowRemote((v) => !v)}
            disabled={!canManage || remoteBusy}
            className="flex items-start justify-between gap-3 rounded border border-[#323232] bg-[#2b2b2b] px-3 py-2 text-left transition-colors hover:border-[#5394ec] hover:bg-[#4c5052] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-[#e8e8e8]">
                {remoteBusy ? 'Downloading…' : 'Pull a curated dataset from GitHub'}
              </div>
              <div className="mt-0.5 text-[11px] text-[#808080]">
                Real-world sample databases (Chinook, Northwind) — fetched directly from
                raw.githubusercontent.com.
              </div>
            </div>
            <span className="shrink-0 self-center text-[#606366]">{showRemote ? '▾' : '▸'}</span>
          </button>
          {showRemote && (
            <div className="ml-3 flex flex-col gap-1.5 border-l-2 border-[#5394ec]/40 pl-2">
              {REMOTE_DATASETS.map((ds) => (
                <button
                  key={ds.id}
                  type="button"
                  onClick={() => onPickRemote(ds)}
                  disabled={remoteBusy}
                  className="flex items-start justify-between gap-2 rounded border border-[#323232] bg-[#2b2b2b] px-2.5 py-1.5 text-left transition-colors hover:border-[#5394ec] hover:bg-[#4c5052] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[12px] font-semibold text-[#e8e8e8]">{ds.name}</span>
                      <span className="font-mono text-[10px] text-[#808080]">
                        ~{formatBytes(ds.bytes)} · {ds.license}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-[#808080]">{ds.description}</div>
                  </div>
                </button>
              ))}
              <div className="mt-1 rounded border border-[#323232] bg-[#2b2b2b] p-2">
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#808080]">
                  Or paste a GitHub URL
                </label>
                <div className="flex gap-1">
                  <input
                    type="url"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitCustom();
                    }}
                    placeholder="github.com/owner/repo/blob/main/file.db"
                    disabled={remoteBusy}
                    className="min-w-0 flex-1 rounded border border-[#323232] bg-[#3c3f41] px-2 py-1 font-mono text-[11px] text-[#e8e8e8] outline-none focus:border-[#5394ec] disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={submitCustom}
                    disabled={!customUrl.trim() || remoteBusy}
                    className="rounded bg-[#499c54] px-2 py-1 text-[11px] text-white hover:bg-[#5fb069] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Fetch
                  </button>
                </div>
                <p className="mt-1 text-[10px] leading-snug text-[#808080]">
                  <code>/blob/</code> URLs auto-rewrite. Other hosts must allow CORS.
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={!canManage}
            className="flex items-start justify-between gap-3 rounded border border-[#323232] bg-[#2b2b2b] px-3 py-2 text-left transition-colors hover:border-[#5394ec] hover:bg-[#4c5052] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-[#e8e8e8]">Import a SQLite file</div>
              <div className="mt-0.5 text-[11px] text-[#808080]">
                Pick a <code>.db</code>, <code>.sqlite</code>, or <code>.sqlite3</code> file from
                your computer — it&apos;s read into browser storage as-is.
              </div>
            </div>
            <span className="shrink-0 self-center text-[#606366]">→</span>
          </button>
        </div>
        <p className="mt-4 text-[10px] leading-relaxed text-[#606366]">
          Everything runs locally. Your queries and data never leave the browser. The only network
          call YesQL makes is when you pick a curated dataset above — and even then, only the file
          you picked gets downloaded.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".db,.sqlite,.sqlite3,application/x-sqlite3,application/octet-stream"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImport(file);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}
